import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";
import { normalizeUrl, isValidHttpUrl } from "../../utils/normalizeUrl";

/* =========================
   CONFIG
========================= */
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/* =========================
   RISK BAR HELPER
========================= */
function riskColor(score) {
  if (score >= 80) return "#dc2626"; // red
  if (score >= 60) return "#f59e0b"; // amber
  return "#16a34a"; // green
}

function Claims() {
  const { user } = useAuth();

  const [claims, setClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(true);

  const [groupedClaims, setGroupedClaims] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const [evidences, setEvidences] = useState({});
  const [formState, setFormState] = useState({});
  const [fileState, setFileState] = useState({});
  const [uploading, setUploading] = useState({});

  /* =========================
     LOAD CLAIMS
  ========================= */
  useEffect(() => {
    const loadClaims = async () => {
      try {
        setClaimsLoading(true);

        const q = query(
          collection(db, "claims"),
          where("userId", "==", user.uid)
        );
        const snap = await getDocs(q);

        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setClaims(list);

        const grouped = {};
        list.forEach((c) => {
          const group = c.group || "Other";
          if (!grouped[group]) grouped[group] = [];
          grouped[group].push(c);
        });

        setGroupedClaims(grouped);

        const collapsed = {};
        Object.keys(grouped).forEach((g) => {
          collapsed[g] = false;
        });
        setCollapsedGroups(collapsed);
      } finally {
        setClaimsLoading(false);
      }
    };

    if (user?.uid) loadClaims();
  }, [user]);

  /* =========================
     LOAD EVIDENCES
  ========================= */
  const loadEvidences = async () => {
    const q = query(
      collection(db, "evidences"),
      where("userId", "==", user.uid)
    );
    const snap = await getDocs(q);

    const grouped = {};
    snap.docs.forEach((d) => {
      const data = d.data();
      if (!grouped[data.claimId]) grouped[data.claimId] = [];
      grouped[data.claimId].push({ id: d.id, ...data });
    });

    setEvidences(grouped);
  };

  useEffect(() => {
    loadEvidences();
  }, [user]);

  /* =========================
     ADD LINK EVIDENCE
  ========================= */
  const addLinkEvidence = async (claimId) => {
    const data = formState[claimId];

    if (!data?.label || !data?.url) {
      alert("Please enter both label and URL.");
      return;
    }

    const existingLink = (evidences[claimId] || []).some(
      (ev) => ev.type === "link"
    );
    if (existingLink) {
      alert("Only one URL evidence is allowed per claim.");
      return;
    }

    if (!isValidHttpUrl(data.url)) {
      alert("Please enter a valid URL starting with https://");
      return;
    }

    await addDoc(collection(db, "evidences"), {
      userId: user.uid,
      claimId,
      type: "link",
      label: data.label,
      url: normalizeUrl(data.url),
      createdAt: serverTimestamp(),
    });

    setFormState((p) => ({ ...p, [claimId]: { label: "", url: "" } }));
    loadEvidences();
  };

  /* =========================
     ADD FILE EVIDENCE
  ========================= */
  const addFileEvidence = async (claimId) => {
    const file = fileState[claimId];
    const label = formState[claimId]?.fileLabel;

    if (!file || !label) {
      alert("Please select a file and enter a label.");
      return;
    }

    const existingFile = (evidences[claimId] || []).some(
      (ev) => ev.type === "file"
    );
    if (existingFile) {
      alert("Only one file evidence is allowed per claim.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      alert("File size exceeds 5 MB limit.");
      return;
    }

    try {
      setUploading((p) => ({ ...p, [claimId]: true }));

      const storageRef = ref(
        storage,
        `evidences/${user.uid}/${claimId}/${Date.now()}_${file.name}`
      );

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      await addDoc(collection(db, "evidences"), {
        userId: user.uid,
        claimId,
        type: "file",
        label,
        fileName: file.name,
        fileUrl: downloadURL,
        createdAt: serverTimestamp(),
      });

      setFileState((p) => ({ ...p, [claimId]: null }));
      setFormState((p) => ({
        ...p,
        [claimId]: { ...p[claimId], fileLabel: "" },
      }));

      loadEvidences();
    } finally {
      setUploading((p) => ({ ...p, [claimId]: false }));
    }
  };

  /* =========================
     DELETE EVIDENCE
  ========================= */
  const deleteEvidence = async (evidenceId) => {
    if (!window.confirm("Delete this evidence?")) return;
    await deleteDoc(doc(db, "evidences", evidenceId));
    loadEvidences();
  };

  /* =========================
     UI
  ========================= */
  return (
    <div>
      <h1
        style={{ marginBottom: 24 }}
        title="Claims are statements extracted from your pitch that require supporting evidence."
      >
        Claims
      </h1>

      {!claimsLoading && claims.length === 0 && (
        <p style={{ color: "#64748b" }}>
          No claims available. Analyze your pitch to generate claims.
        </p>
      )}

      {Object.entries(groupedClaims).map(([group, groupClaims]) => (
        <div key={group} style={{ marginBottom: 32 }}>
          <div
            style={{
              cursor: "pointer",
              fontSize: 16,
              fontWeight: 600,
              marginBottom: 12,
            }}
            title="Claims are grouped by theme for easier review."
            onClick={() =>
              setCollapsedGroups((p) => ({
                ...p,
                [group]: !p[group],
              }))
            }
          >
            {collapsedGroups[group] ? "▶" : "▼"} {group} (
            {groupClaims.length})
          </div>

          {!collapsedGroups[group] &&
            groupClaims.map((claim) => (
              <div
                key={claim.id}
                className="card"
                style={{ padding: 24, marginBottom: 24 }}
                title="Evaluators will review this claim alongside the evidence you attach."
              >
                <div
                  style={{ fontSize: 16, fontWeight: 600 }}
                  title="This is the claim extracted from your pitch."
                >
                  {claim.text}
                </div>

                <div
                  style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}
                  title="AI risk reflects how ambiguous or unsupported this claim appears — not whether it is true."
                >
                  AI Risk level:{" "}
                  <strong>{claim.riskLevel.toUpperCase()}</strong>
                </div>

                {typeof claim.riskScore === "number" && (
                  <div
                    style={{ marginTop: 12 }}
                    title="Higher risk usually means the claim needs clearer wording or stronger evidence."
                  >
                    <div
                      style={{
                        height: 8,
                        background: "#e5e7eb",
                        borderRadius: 4,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${claim.riskScore}%`,
                          height: "100%",
                          background: riskColor(claim.riskScore),
                        }}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        marginTop: 6,
                        color: "#475569",
                      }}
                    >
                      Risk score: <strong>{claim.riskScore}/100</strong> —{" "}
                      {claim.riskReason}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 20 }}>
                  <strong
                    title="Evidence helps evaluators understand why this claim is credible."
                  >
                    Evidence
                  </strong>

                  {(evidences[claim.id] || []).length === 0 && (
                    <div
                      style={{
                        fontSize: 13,
                        color: "#94a3b8",
                        marginTop: 8,
                      }}
                    >
                      No evidence attached yet.
                    </div>
                  )}

                  {(evidences[claim.id] || []).map((ev) => (
                    <div key={ev.id} style={{ marginTop: 8 }}>
                      <a
                        href={`/evidence/${ev.id}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Evaluators can open this evidence in a new tab."
                      >
                        {ev.type === "link" ? "🔗" : "📄"} {ev.label}
                      </a>

                      <button
                        className="btn-secondary"
                        style={{ marginLeft: 8 }}
                        onClick={() => deleteEvidence(ev.id)}
                        title="You can remove or replace evidence at any time."
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>

                <div
                  style={{ marginTop: 20 }}
                  title="Use a link for public sources like articles, websites, or repositories."
                >
                  <input
                    placeholder="Link label"
                    value={formState[claim.id]?.label || ""}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        [claim.id]: {
                          ...p[claim.id],
                          label: e.target.value,
                        },
                      }))
                    }
                  />
                  <input
                    placeholder="URL"
                    value={formState[claim.id]?.url || ""}
                    style={{ marginLeft: 8 }}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        [claim.id]: {
                          ...p[claim.id],
                          url: e.target.value,
                        },
                      }))
                    }
                  />
                  <button
                    className="btn-primary"
                    style={{ marginLeft: 8 }}
                    onClick={() => addLinkEvidence(claim.id)}
                    title="Only one link is allowed to encourage clear, high-quality sourcing."
                  >
                    Add Link
                  </button>
                </div>

                <div
                  style={{ marginTop: 12 }}
                  title="Use a file for internal documents like reports, metrics, or signed proofs."
                >
                  <input
                    placeholder="File label"
                    value={formState[claim.id]?.fileLabel || ""}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        [claim.id]: {
                          ...p[claim.id],
                          fileLabel: e.target.value,
                        },
                      }))
                    }
                  />
                  <input
                    type="file"
                    style={{ marginLeft: 8 }}
                    onChange={(e) =>
                      setFileState((p) => ({
                        ...p,
                        [claim.id]: e.target.files[0],
                      }))
                    }
                  />
                  <button
                    className="btn-primary"
                    style={{ marginLeft: 8 }}
                    disabled={uploading[claim.id]}
                    onClick={() => addFileEvidence(claim.id)}
                    title="Only one file is allowed to keep evidence focused and reviewable."
                  >
                    {uploading[claim.id] ? "Uploading…" : "Upload File"}
                  </button>
                </div>
              </div>
            ))}
        </div>
      ))}
    </div>
  );
}

export default Claims;
